import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Transfer, message, Spin, Select, Row, Col } from 'antd';
import axiosInstance from '../../services/axiosServicesVisit';

// Fonction pour extraire la marque (lettres au début du code)
const getBrandFromCode = (code) => {
    if (!code) return 'SANS MARQUE';
    // Regex : Prend les lettres au début. Ex: "dh149" -> "DH"
    const match = String(code).match(/^([a-zA-Z]+)/);
    return match ? match[1].toUpperCase() : 'AUTRE';
};

const AssignProductsModal = ({ visible, onCancel, delegate }) => {
    const [targetKeys, setTargetKeys] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    
    // État pour le filtre de marque
    const [selectedBrand, setSelectedBrand] = useState(null);

    useEffect(() => {
        if (visible && delegate) {
            setLoading(true);
            setSelectedBrand(null);

            const fetchProducts = axiosInstance.get('/v1/products?page=1&limit=2000');
            const fetchDelegateDetails = axiosInstance.get(`/v1/delegates/${delegate.id}`);

            Promise.all([fetchProducts, fetchDelegateDetails])
                .then(([productsRes, delegateRes]) => {
                    const rawProducts = productsRes.data?.data || [];
                    
                    // --- CORRECTION ICI ---
                    const all = rawProducts.map(p => {
                        // Dans votre base de données, c'est 'productCode', pas 'code'
                        const codeValue = p.productCode; 

                        return {
                            key: p.id,
                            title: p.name,
                            code: codeValue, 
                            brand: getBrandFromCode(codeValue)
                        };
                    });
                    
                    setAllProducts(all);
                    
                    const assignedIds = delegateRes.data?.products?.map(p => p.id) || [];
                    setTargetKeys(assignedIds);
                })
                .catch((err) => {
                    console.error(err);
                    message.error("Erreur de chargement des données.");
                })
                .finally(() => setLoading(false));
        }
    }, [visible, delegate]);

    // 1. Calculer les marques uniques
    const brandOptions = useMemo(() => {
        const uniqueBrands = [...new Set(allProducts.map(p => p.brand))].sort();
        return uniqueBrands.map(brand => ({ label: brand, value: brand }));
    }, [allProducts]);

    // 2. Filtrer la liste
    const filteredDataSource = useMemo(() => {
        if (!selectedBrand) return allProducts;

        return allProducts.filter(p => 
            p.brand === selectedBrand || 
            targetKeys.includes(p.key) // Garder les éléments déjà assignés visibles
        );
    }, [allProducts, selectedBrand, targetKeys]);

    const handleChange = (newTargetKeys) => {
        setTargetKeys(newTargetKeys);
    };

    const handleOk = async () => {
        setSubmitLoading(true);
        try {
            await axiosInstance.post(`/v1/delegates/${delegate.id}/assign-products`, {
                productIds: targetKeys
            });
            message.success("Assignation mise à jour.");
            onCancel();
        } catch (error) {
            message.error("Erreur lors de la mise à jour.");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal
            title={`Assigner des Produits à ${delegate?.firstname} ${delegate?.lastname}`}
            open={visible}
            onCancel={onCancel}
            onOk={handleOk}
            width={900}
            confirmLoading={submitLoading}
            destroyOnClose
            style={{ top: 20 }}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}><Spin /></div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    {/* Filtre */}
                    <div style={{ background: '#f5f5f5', padding: '10px', borderRadius: '6px' }}>
                        <Row align="middle">
                            <Col flex="150px">
                                <span style={{ fontWeight: 'bold' }}>Filtrer par Marque&nbsp;:</span>
                            </Col>
                            <Col flex="auto">
                                <Select
                                    style={{ width: '100%', maxWidth: '300px' }}
                                    placeholder="Sélectionner une marque..."
                                    allowClear
                                    showSearch
                                    value={selectedBrand}
                                    onChange={setSelectedBrand}
                                    options={brandOptions}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Col>
                        </Row>
                    </div>

                    {/* Liste */}
                    <Transfer
                        dataSource={filteredDataSource}
                        targetKeys={targetKeys}
                        onChange={handleChange}
                        render={item => (
                            <span>
                                {item.title} 
                                {item.code && <span style={{ color: '#999', fontSize: '0.85em', marginLeft: 5 }}>
                                     ({item.code})
                                </span>}
                            </span>
                        )}
                        pagination
                        showSearch
                        listStyle={{ width: '47%', height: 400 }}
                        titles={[`Disponibles (${filteredDataSource.length})`, `Assignés (${targetKeys.length})`]}
                        filterOption={(inputValue, option) => {
                            const text = inputValue.toLowerCase();
                            return (
                                option.title.toLowerCase().includes(text) ||
                                (option.code && option.code.toLowerCase().includes(text)) ||
                                (option.brand && option.brand.toLowerCase().includes(text))
                            );
                        }}
                    />
                </div>
            )}
        </Modal>
    );
};

export default AssignProductsModal;